import { Add, ArrowDown, ArrowUp, TrashCan } from '@carbon/icons-react';
import {
  Button,
  ComboBox,
  IconButton,
  RadioButton,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
} from '@carbon/react';
import { asProblem, crudApi, EmptyState, hasPermission, problemMessage, useAdmin, type RecordValues } from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../../api';
import { TableLoading } from '../../common/Loading';
import { ErrorNotice } from '../../common/Notices';
import { SaveBar } from '../../common/SaveBar';
import {
  addDimension,
  addMember,
  addValue,
  fromVariants,
  isDirty,
  moveMember,
  problems,
  removeDimension,
  removeMember,
  removeValue,
  setDefault,
  setMemberValue,
  toRequest,
  updateDimension,
  updateValue,
  type DraftValue,
  type SavedVariants,
  type VariantDraft,
} from './variantDraft';
import './itemGroups.scss';

interface ItemRow {
  id: string;
  version: number | string;
  code: string;
  name: string;
}

const DISPLAYS = ['buttons', 'swatches', 'list'] as const;

type ValueField = keyof Omit<DraftValue, 'key'>;
/** The columns of a dimension's values; the swatch only for a dimension shown as swatches. */
const valueFields = (display: string): ValueField[] =>
  display === 'swatches' ? ['code', 'label', 'labelEn', 'shortLabel', 'swatchColor'] : ['code', 'label', 'labelEn', 'shortLabel'];

async function loadVariants(id: string): Promise<SavedVariants> {
  const { data, error } = await hoApi.GET('/api/v1/catalog/item-groups/{id}/variants', { params: { path: { id } } });
  if (!data) throw error;
  return data as SavedVariants;
}

/**
 * An item group's variants (ADR 0052, FR-MENU-11): its dimensions (size, colour…) with their values, and the items that
 * are its variants, each with one value per dimension and one of them the default. Nothing is saved until Save, which
 * sends the whole set in one request; the server checks every rule again.
 */
export function ItemGroupVariantsTab({ group }: { group: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, 'ho.catalog.item_groups.manage');
  const id = String(group.id);
  const brandId = String(group.brandId);
  const queryClient = useQueryClient();
  const key = ['ho', 'item-group-variants', id];
  const saved = useQuery({ queryKey: key, queryFn: () => loadVariants(id) });
  const [draft, setDraft] = useState<{ source: unknown; value: VariantDraft }>();
  const value = draft && draft.source === saved.data ? draft.value : saved.data ? fromVariants(saved.data) : { dimensions: [], members: [] };
  const change = (next: (d: VariantDraft) => VariantDraft) => setDraft({ source: saved.data, value: next(value) });
  const [search, setSearch] = useState('');
  const items = useQuery({
    queryKey: ['ho', 'item-group-items', brandId, search],
    enabled: canEdit && search.trim().length > 0,
    queryFn: () =>
      crudApi<ItemRow, never>(hoApi, '/api/v1/catalog/items').list({ search: search.trim(), brandId, itemType: 'sellable', pageSize: 20 }),
  });

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.PUT('/api/v1/catalog/item-groups/{id}/variants', {
        params: { path: { id } },
        headers: { 'If-Match': `"${String(saved.data?.version ?? '')}"` },
        body: toRequest(value),
      });
      if (!data) throw error;
      return data as SavedVariants;
    },
    onSuccess: (next) => {
      setDraft(undefined);
      queryClient.setQueryData(key, next);
      void queryClient.invalidateQueries({ queryKey: ['resource'] });
    },
  });

  if (saved.isLoading) return <TableLoading columns={4} label={t('common.loading')} />;
  if (!saved.data) return <ErrorNotice title={problemMessage(t, asProblem(saved.error))} onRetry={() => void saved.refetch()} />;

  const found = problems(value);
  const dirty = isDirty(value, saved.data);
  const conflict = (save.error as ApiProblem | null)?.code === 'common.version_conflict';

  return (
    <div className="pos-item-group">
      <p className="pos-item-group__hint">{t('itemGroups.hint')}</p>

      <section className="pos-item-group__section" aria-labelledby="ig-dimensions">
        <h3 id="ig-dimensions" className="pos-item-group__heading">
          {t('itemGroups.dimensions')}
        </h3>
        {value.dimensions.length === 0 && <EmptyState heading={t('itemGroups.noDimensions')} description={t('itemGroups.noDimensionsHelp')} />}
        {value.dimensions.map((d, di) => (
          <div key={d.key} className="pos-item-group__dimension">
            <div className="pos-item-group__fields">
              <TextInput
                id={`${d.key}-code`}
                labelText={t('md.fields.code')}
                value={d.code}
                readOnly={!canEdit}
                onChange={(e) => change((v) => updateDimension(v, d.key, { code: e.target.value }))}
              />
              <TextInput
                id={`${d.key}-name`}
                labelText={t('md.fields.name')}
                value={d.name}
                readOnly={!canEdit}
                onChange={(e) => change((v) => updateDimension(v, d.key, { name: e.target.value }))}
              />
              <TextInput
                id={`${d.key}-name-en`}
                labelText={t('itemGroups.nameEn')}
                value={d.nameEn}
                readOnly={!canEdit}
                onChange={(e) => change((v) => updateDimension(v, d.key, { nameEn: e.target.value }))}
              />
              <Select
                id={`${d.key}-display`}
                labelText={t('itemGroups.display')}
                value={d.display}
                disabled={!canEdit}
                onChange={(e) => change((v) => updateDimension(v, d.key, { display: e.target.value }))}
              >
                {DISPLAYS.map((option) => (
                  <SelectItem key={option} value={option} text={t(`md.enums.display.${option}` as I18nKey)} />
                ))}
              </Select>
              {canEdit && (
                <IconButton
                  kind="ghost"
                  label={t('itemGroups.removeDimension', { name: d.name || di + 1 })}
                  onClick={() => change((v) => removeDimension(v, d.key))}
                >
                  <TrashCan />
                </IconButton>
              )}
            </div>
            <TableContainer>
              <Table size="sm" aria-label={t('itemGroups.valuesOf', { name: d.name || di + 1 })}>
                <TableHead>
                  <TableRow>
                    <TableHeader>{t('itemGroups.valueField.code')}</TableHeader>
                    <TableHeader>{t('itemGroups.valueField.label')}</TableHeader>
                    <TableHeader>{t('itemGroups.valueField.labelEn')}</TableHeader>
                    <TableHeader>{t('itemGroups.valueField.shortLabel')}</TableHeader>
                    {d.display === 'swatches' && <TableHeader>{t('itemGroups.valueField.swatchColor')}</TableHeader>}
                    {canEdit && <TableHeader aria-label={t('admin.actions')} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.values.map((v, vi) => (
                    <TableRow key={v.key}>
                      {valueFields(d.display).map((field) => (
                        <TableCell key={field}>
                          <TextInput
                            id={`${v.key}-${field}`}
                            labelText={`${t(`itemGroups.valueField.${field}` as I18nKey)} ${vi + 1}`}
                            hideLabel
                            size="sm"
                            value={v[field]}
                            readOnly={!canEdit}
                            placeholder={field === 'swatchColor' ? '#C73F00' : undefined}
                            onChange={(e) => change((x) => updateValue(x, d.key, v.key, { [field]: e.target.value }))}
                          />
                        </TableCell>
                      ))}
                      {canEdit && (
                        <TableCell className="pos-admin-page__actions">
                          <IconButton
                            kind="ghost"
                            size="sm"
                            label={t('itemGroups.removeValue', { name: v.label || v.code || vi + 1 })}
                            onClick={() => change((x) => removeValue(x, d.key, v.key))}
                          >
                            <TrashCan />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {canEdit && (
              <Button kind="ghost" size="sm" renderIcon={Add} onClick={() => change((x) => addValue(x, d.key))}>
                {t('itemGroups.addValue')}
              </Button>
            )}
          </div>
        ))}
        {canEdit && (
          <Button
            kind="tertiary"
            renderIcon={Add}
            onClick={() =>
              change((x) =>
                addDimension(x, x.dimensions.length === 0 ? 'size' : `dim${x.dimensions.length + 1}`, x.dimensions.length === 0 ? t('itemGroups.size') : ''),
              )
            }
          >
            {t('itemGroups.addDimension')}
          </Button>
        )}
      </section>

      <section className="pos-item-group__section" aria-labelledby="ig-members">
        <h3 id="ig-members" className="pos-item-group__heading">
          {t('itemGroups.members')}
        </h3>
        <TableContainer>
          <Table aria-label={t('itemGroups.members')}>
            <TableHead>
              <TableRow>
                <TableHeader>{t('md.fields.item_id')}</TableHeader>
                {value.dimensions.map((d) => (
                  <TableHeader key={d.key}>{d.name || d.code}</TableHeader>
                ))}
                <TableHeader>{t('md.fields.is_default')}</TableHeader>
                {canEdit && <TableHeader aria-label={t('admin.actions')} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {value.members.map((m, index) => (
                <TableRow key={m.key}>
                  <TableCell>{m.itemLabel}</TableCell>
                  {value.dimensions.map((d) => (
                    <TableCell key={d.key}>
                      <Select
                        id={`${m.key}-${d.key}`}
                        labelText={t('itemGroups.valueOf', { dimension: d.name || d.code, item: m.itemLabel })}
                        hideLabel
                        size="sm"
                        value={m.values[d.key] ?? ''}
                        disabled={!canEdit}
                        invalid={!d.values.some((v) => v.key === m.values[d.key])}
                        invalidText={t('itemGroups.problem.memberValue')}
                        onChange={(e) => change((x) => setMemberValue(x, m.key, d.key, e.target.value))}
                      >
                        <SelectItem value="" text={t('itemGroups.choose')} disabled hidden />
                        {d.values.map((v) => (
                          <SelectItem key={v.key} value={v.key} text={v.label || v.code} />
                        ))}
                      </Select>
                    </TableCell>
                  ))}
                  <TableCell>
                    <RadioButton
                      id={`${m.key}-default`}
                      name="ig-default"
                      labelText={t('itemGroups.defaultOf', { item: m.itemLabel })}
                      hideLabel
                      checked={m.isDefault}
                      disabled={!canEdit}
                      onChange={() => change((x) => setDefault(x, m.key))}
                    />
                  </TableCell>
                  {canEdit && (
                    <TableCell className="pos-admin-page__actions">
                      <IconButton kind="ghost" size="sm" label={t('itemGroups.moveUp', { item: m.itemLabel })} disabled={index === 0} onClick={() => change((x) => moveMember(x, m.key, -1))}>
                        <ArrowUp />
                      </IconButton>
                      <IconButton
                        kind="ghost"
                        size="sm"
                        label={t('itemGroups.moveDown', { item: m.itemLabel })}
                        disabled={index === value.members.length - 1}
                        onClick={() => change((x) => moveMember(x, m.key, 1))}
                      >
                        <ArrowDown />
                      </IconButton>
                      <IconButton kind="ghost" size="sm" label={t('itemGroups.removeMember', { item: m.itemLabel })} onClick={() => change((x) => removeMember(x, m.key))}>
                        <TrashCan />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {value.members.length === 0 && <EmptyState heading={t('itemGroups.noMembers')} description={t('itemGroups.noMembersHelp')} />}
        </TableContainer>
        {canEdit && (
          <div className="pos-item-group__add">
            <ComboBox<ItemRow>
              id="ig-add-item"
              titleText={t('itemGroups.addMember')}
              placeholder={t('itemGroups.searchItem')}
              items={(items.data?.items ?? []).filter((i) => !value.members.some((m) => m.itemId === i.id))}
              itemToString={(i) => (i ? `${i.code} · ${i.name}` : '')}
              onInputChange={(text) => setSearch(text)}
              selectedItem={null}
              onChange={({ selectedItem }) => {
                if (selectedItem) change((x) => addMember(x, selectedItem.id, `${selectedItem.code} · ${selectedItem.name}`));
                setSearch('');
              }}
            />
          </div>
        )}
      </section>

      {canEdit && dirty && (
        <SaveBar
          summary={t('itemGroups.unsaved')}
          problems={[
            ...[...found].map((p) => t(`itemGroups.problem.${p}` as I18nKey)),
            ...(save.error ? [conflict ? t('itemGroups.conflict') : problemMessage(t, asProblem(save.error))] : []),
          ]}
          discardText={t('itemGroups.discard')}
          saveText={t('itemGroups.save')}
          saveDisabled={found.size > 0}
          saving={save.isPending}
          onDiscard={() => {
            save.reset();
            setDraft(undefined);
            if (conflict) void saved.refetch();
          }}
          onSave={() => save.mutate()}
        />
      )}
    </div>
  );
}
